<template>
    <v-sheet
        color="primary"
        elevation="8"
        rounded="sm"
        class="trip-duration-panel pa-3"
        @click.stop
        @mousedown.stop
        @touchstart.stop
    >
        <v-text-field
            :model-value="localSpeed"
            type="number"
            :label="$t('formFields.avgSpeedKmh')"
            density="compact"
            hide-details="auto"
            variant="outlined"
            color="yellow"
            base-color="yellow"
            class="trip-duration-panel__speed-input"
            :readonly="speedReadonly"
            @update:model-value="onSpeedInput"
        />

        <div class="text-body-2 font-weight-bold text-yellow mt-3 mb-2">
            {{ routeLabel }}
        </div>
        <div class="text-body-2 text-white mb-1">
            <span class="text-white">{{ $t("entries.totalDriveTime") }}:&nbsp;</span>
            <span v-if="driveTimeLabel" class="text-yellow">{{ driveTimeLabel }}</span>
        </div>
        <div class="text-body-2 text-white mb-1">
            <span class="text-white">{{ $t("entries.totalRestTime") }}:&nbsp;</span>
            <span v-if="restTimeLabel" class="text-yellow">{{ restTimeLabel }}</span>
        </div>
        <div class="text-body-2 text-white mb-3">
            <span class="text-white">{{ $t("entries.totalTripTime") }}:&nbsp;</span>
            <span v-if="tripTimeLabel" class="text-yellow">{{ tripTimeLabel }}</span>
        </div>
        <div class="text-caption text-white trip-duration-panel__explanation">
            {{ $t("entries.distanceCalculatorExplanation") }}
        </div>
    </v-sheet>
</template>

<script>
import { formatDurationCompact } from "@/utils";
import {
    computeEtaBreakdown,
    formatDurationCompactAllowZero,
} from "@/utils/etaCalculation";

export default {
    name: "TripDurationPanel",
    props: {
        entry: {
            type: Object,
            required: true,
        },
        speedKmh: {
            type: [String, Number],
            default: "",
        },
        speedReadonly: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["update:speedKmh"],
    computed: {
        localSpeed() {
            return this.speedKmh;
        },
        breakdown() {
            const speed = Number(this.localSpeed);
            if (!Number.isFinite(speed) || speed <= 0) return null;
            return computeEtaBreakdown({
                distanceKm: this.entry.distance_km,
                avgSpeedKmh: speed,
            });
        },
        routeLabel() {
            const loadingCity = this.entry.loading_location?.cityName || "—";
            const unloadingCity = this.entry.unloading_location?.cityName || "—";
            return `${loadingCity} → ${unloadingCity}`;
        },
        driveTimeLabel() {
            if (!this.breakdown) return null;
            return formatDurationCompact(this.breakdown.drivingSeconds);
        },
        restTimeLabel() {
            if (!this.breakdown) return null;
            return formatDurationCompactAllowZero(
                this.breakdown.totalRestSeconds,
                formatDurationCompact
            );
        },
        tripTimeLabel() {
            if (!this.breakdown) return null;
            return formatDurationCompact(this.breakdown.totalTripSeconds);
        },
    },
    methods: {
        onSpeedInput(value) {
            if (!this.speedReadonly) {
                this.$emit("update:speedKmh", value);
            }
        },
    },
};
</script>

<style scoped>
.trip-duration-panel {
    width: 320px;
    max-width: min(320px, calc(100vw - 24px));
    border: 1px solid rgba(255, 235, 59, 0.35);
}

.trip-duration-panel__explanation {
    opacity: 0.9;
    line-height: 1.4;
}

.trip-duration-panel__speed-input :deep(input[type="number"]::-webkit-inner-spin-button),
.trip-duration-panel__speed-input :deep(input[type="number"]::-webkit-outer-spin-button) {
    -webkit-appearance: none;
    margin: 0;
}

.trip-duration-panel__speed-input :deep(input[type="number"]) {
    -moz-appearance: textfield;
    appearance: textfield;
}
</style>
